import { useEffect, useState } from "react";
import { CheckCircle2Icon, Loader2Icon, AlertCircleIcon } from "lucide-react";

type SaveState = "idle" | "saving" | "saved" | "error";

/**
 * Auto-save toast. Replaces the subtle "Opgeslagen" text in the nav with a clear,
 * momentary toaster bottom-right: a spinner while saving, a green confirmation that
 * fades after a couple of seconds, and a persistent red toast on failure.
 */
export function SaveToast({ saveState, lastSavedAt }: { saveState: SaveState; lastSavedAt: string | null }) {
  const [visible, setVisible] = useState(false);
  const [kind, setKind] = useState<"saving" | "saved" | "error">("saved");

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (saveState === "saving") {
      // Brief delay so fast saves don't flicker a spinner.
      timer = setTimeout(() => { setKind("saving"); setVisible(true); }, 400);
    } else if (saveState === "saved") {
      setKind("saved");
      setVisible(true);
      timer = setTimeout(() => setVisible(false), 2200);
    } else if (saveState === "error") {
      setKind("error");
      setVisible(true);
    } else {
      setVisible(false);
    }
    return () => { if (timer) clearTimeout(timer); };
  }, [saveState, lastSavedAt]);

  return (
    <div
      aria-live="polite"
      className={`pointer-events-none fixed bottom-5 right-5 z-50 transition-all duration-200 ${visible ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"}`}
    >
      <div
        className={`flex items-center gap-2 rounded-lg border px-3.5 py-2 text-sm shadow-lg ${
          kind === "error"
            ? "border-red-300 bg-red-50 text-red-700"
            : kind === "saving"
              ? "border-border bg-card text-muted-foreground"
              : "border-emerald-300 bg-emerald-50 text-emerald-700"
        }`}
      >
        {kind === "saving" ? (
          <Loader2Icon size={15} className="animate-spin" />
        ) : kind === "error" ? (
          <AlertCircleIcon size={15} />
        ) : (
          <CheckCircle2Icon size={15} />
        )}
        <span className="font-medium">
          {kind === "saving"
            ? "Opslaan..."
            : kind === "error"
              ? "Niet opgeslagen, probeer opnieuw"
              : `Opgeslagen${lastSavedAt ? ` om ${lastSavedAt}` : ""}`}
        </span>
      </div>
    </div>
  );
}
